import type { ScanResult } from '../../../src/station/types';

/**
 * Deterministic architecture map, derived entirely from scan evidence.
 * Written to `.viberaven/architecture-map.md` so agents get structural
 * context (pages, API surface, data layer, providers) before editing —
 * not just a list of gaps.
 */

const MAX_LIST = 30;

type PathEntry = { path: string };

function routeFromPagePath(path: string): string {
  const appMatch = path.match(/(?:^|\/)app\/(.*)\/page\.(tsx|jsx|ts|js)$/) || path.match(/(?:^|\/)app\/page\.(tsx|jsx|ts|js)$/);
  if (appMatch) {
    const segment = appMatch.length > 2 && typeof appMatch[1] === 'string' && !/^(tsx|jsx|ts|js)$/.test(appMatch[1])
      ? appMatch[1]
      : '';
    const cleaned = segment
      .split('/')
      .filter((part) => !(part.startsWith('(') && part.endsWith(')')))
      .join('/');
    return `/${cleaned}`.replace(/\/+/g, '/');
  }
  const pagesMatch = path.match(/(?:^|\/)pages\/(.*)\.(tsx|jsx|ts|js)$/);
  if (pagesMatch) {
    const cleaned = pagesMatch[1].replace(/\/index$/, '') ;
    return `/${cleaned === 'index' ? '' : cleaned}`.replace(/\/+/g, '/');
  }
  return path;
}

function apiRouteFromPath(path: string): string {
  const appApi = path.match(/(?:^|\/)app\/(api\/.*)\/route\.(ts|js|tsx|jsx)$/);
  if (appApi) return `/${appApi[1]}`;
  const pagesApi = path.match(/(?:^|\/)pages\/(api\/.*)\.(ts|js|tsx|jsx)$/);
  if (pagesApi) return `/${pagesApi[1]}`;
  return path;
}

function listSection(title: string, items: string[], emptyLine: string): string {
  const lines = [`## ${title}`, ''];
  if (items.length === 0) {
    lines.push(emptyLine, '');
    return lines.join('\n');
  }
  const shown = items.slice(0, MAX_LIST);
  for (const item of shown) {
    lines.push(`- ${item}`);
  }
  if (items.length > shown.length) {
    lines.push(`- …and ${items.length - shown.length} more`);
  }
  lines.push('');
  return lines.join('\n');
}

function envKeysFromExample(scan: ScanResult): string[] {
  const example = scan.files.find(
    (file) => /(^|\/)\.env\.example$/.test(file.path) && typeof file.content === 'string'
  );
  if (!example || typeof example.content !== 'string') return [];
  return example.content
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#') && line.includes('='))
    .map((line) => line.split('=')[0].trim())
    .filter((key) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(key));
}

function providerAreas(scan: ScanResult): Array<{ area: string; names: string[] }> {
  const signals = scan.stackSignals;
  const areas: Array<{ area: string; names: string[] }> = [];
  const push = (area: string, names: string[]) => {
    const found = names.filter(Boolean);
    if (found.length > 0) areas.push({ area, names: found });
  };
  push('Database', [
    signals.hasSupabase ? 'Supabase' : '',
    signals.hasNeon ? 'Neon' : '',
    signals.hasPlanetScale ? 'PlanetScale' : '',
    signals.hasTurso ? 'Turso' : '',
    signals.hasMongoDb || signals.hasMongoose ? 'MongoDB' : '',
    signals.hasPrisma ? 'Prisma (ORM)' : '',
    signals.hasDrizzle ? 'Drizzle (ORM)' : '',
  ]);
  push('Auth', [
    signals.hasClerk ? 'Clerk' : '',
    signals.hasAuthJs ? 'Auth.js' : '',
    signals.hasSupabase && signals.hasAuth ? 'Supabase Auth' : '',
  ]);
  push('Payments', [
    signals.hasStripe ? 'Stripe' : '',
    signals.hasPolar ? 'Polar' : '',
    signals.hasPaddle ? 'Paddle' : '',
  ]);
  push('Deployment', [
    signals.hasVercel || scan.files.some((f) => /(^|\/)vercel\.json$/.test(f.path)) ? 'Vercel' : '',
    signals.hasDocker ? 'Docker' : '',
  ]);
  push('Monitoring', [
    signals.hasSentry ? 'Sentry' : '',
    signals.hasPostHog ? 'PostHog' : '',
  ]);
  return areas;
}

export type ArchitectureMapInput = {
  scan: ScanResult;
  archetype: string;
  stackDetected: string[];
};

export type ArchitectureData = {
  archetype: string;
  stack: string[];
  totalFilesScanned: number;
  pages: string[];
  apiRoutes: string[];
  webhooks: string[];
  migrations: string[];
  jobs: string[];
  middleware: string[];
  envKeys: string[];
  providers: Array<{ area: string; names: string[] }>;
  modules: Array<{ name: string; files: number; hasPages: boolean; hasApi: boolean; hasData: boolean }>;
};

function detectModules(paths: PathEntry[]): ArchitectureData['modules'] {
  const groups = new Map<string, string[]>();
  for (const file of paths) {
    if (!/\.(ts|tsx|js|jsx|mjs|cjs|sql|py|go|rs)$/.test(file.path)) continue;
    const parts = file.path.split('/');
    let root = parts[0];
    if ((root === 'packages' || root === 'apps' || root === 'services') && parts.length > 2) {
      root = parts[0] + '/' + parts[1];
    }
    if (parts.length < 2 || root.startsWith('.')) continue;
    const list = groups.get(root) || [];
    list.push(file.path);
    groups.set(root, list);
  }
  return [...groups.entries()]
    .filter(([, files]) => files.length >= 4)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 8)
    .map(([name, files]) => ({
      name,
      files: files.length,
      hasPages: files.some((p) => /(^|\/)(app|pages)\/.*\.(tsx|jsx)$/.test(p) || /page\.(tsx|jsx)$/.test(p)),
      hasApi: files.some((p) => /(^|\/)api\//.test(p) || /route\.(ts|js)$/.test(p) || /(^|\/)server\//.test(p)),
      hasData: files.some((p) => /migrations\/|schema\.|\.sql$/.test(p)),
    }));
}

export function buildArchitectureData(input: ArchitectureMapInput): ArchitectureData {
  const { scan, archetype, stackDetected } = input;
  const pathSet = new Set<string>(scan.files.map((file) => file.path));
  for (const line of (scan.fileTree || '').split('\n')) {
    const trimmed = line.trim().replace(/\\/g, '/');
    if (trimmed) pathSet.add(trimmed);
  }
  const paths: PathEntry[] = [...pathSet].map((path) => ({ path }));

  const pagePaths = paths.filter((f) =>
    /(^|\/)(app|src\/app)\/(.*\/)?page\.(tsx|jsx|ts|js)$/.test(f.path) ||
    (/(^|\/)pages\/.*\.(tsx|jsx|ts|js)$/.test(f.path) && !/(^|\/)pages\/api\//.test(f.path) && !/_app|_document/.test(f.path))
  );
  const apiPaths = paths.filter((f) =>
    /(^|\/)(app|src\/app)\/api\/.*\/route\.(ts|js|tsx|jsx)$/.test(f.path) ||
    /(^|\/)pages\/api\/.*\.(ts|js|tsx|jsx)$/.test(f.path)
  );
  const migrationPaths = paths.filter((f) => /(^|\/)(supabase\/migrations|prisma\/migrations|drizzle)\//.test(f.path));
  const middlewarePaths = paths.filter((f) => /(^|\/)middleware\.(ts|js)$/.test(f.path));
  const jobPaths = paths.filter((f) => /(cron|jobs?|queue|worker)s?\//i.test(f.path) && /\.(ts|js|tsx)$/.test(f.path));
  const webhookPaths = apiPaths.filter((f) => /webhook/i.test(f.path));

  return {
    archetype,
    stack: stackDetected,
    totalFilesScanned: scan.totalFilesScanned,
    pages: [...new Set(pagePaths.map((f) => routeFromPagePath(f.path)))].sort(),
    apiRoutes: [...new Set(apiPaths.map((f) => apiRouteFromPath(f.path)))].sort(),
    webhooks: [...new Set(webhookPaths.map((f) => apiRouteFromPath(f.path)))].sort(),
    migrations: migrationPaths.map((f) => f.path).sort(),
    jobs: [...new Set(jobPaths.map((f) => f.path))].sort(),
    middleware: middlewarePaths.map((f) => f.path),
    envKeys: envKeysFromExample(scan),
    providers: providerAreas(scan),
    modules: detectModules(paths),
  };
}

export function buildArchitectureMap(input: ArchitectureMapInput): string {
  const { scan, archetype, stackDetected } = input;
  const data = buildArchitectureData(input);
  const { pages, apiRoutes, migrations, envKeys, providers } = data;
  const middleware = data.middleware.map((path) => ({ path }));
  const jobPaths = data.jobs.map((path) => ({ path }));
  const webhookPaths = data.webhooks.map((path) => ({ path }));

  const lines: string[] = [
    '# VibeRaven Architecture Map',
    '',
    `Generated from local repo evidence (${scan.totalFilesScanned} files scanned). No cloud, no login.`,
    '',
    `- **Archetype:** ${archetype}`,
    `- **Stack:** ${stackDetected.length > 0 ? stackDetected.join(', ') : 'unknown'}`,
    `- **Middleware:** ${middleware.length > 0 ? middleware.map((f) => f.path).join(', ') : 'none detected'}`,
    '',
  ];

  lines.push(listSection('Pages / User Surface', pages, 'No page routes detected.'));
  lines.push(listSection('API Surface', apiRoutes, 'No API routes detected.'));
  if (webhookPaths.length > 0) {
    lines.push(listSection('Webhook Handlers', webhookPaths.map((f) => apiRouteFromPath(f.path)), ''));
  }
  lines.push(listSection('Data Layer / Migrations', migrations, 'No migrations detected — schema history is not in the repo.'));
  if (jobPaths.length > 0) {
    lines.push(listSection('Background Jobs / Workers', [...new Set(jobPaths.map((f) => f.path))].sort(), ''));
  }

  lines.push('## Providers');
  lines.push('');
  if (providers.length === 0) {
    lines.push('No third-party providers detected from repo evidence.');
  } else {
    for (const entry of providers) {
      lines.push(`- **${entry.area}:** ${entry.names.join(', ')}`);
    }
  }
  lines.push('');

  lines.push('## Environment Contract');
  lines.push('');
  if (envKeys.length === 0) {
    lines.push('No `.env.example` contract found.');
  } else {
    lines.push(`\`.env.example\` documents ${envKeys.length} variable${envKeys.length === 1 ? '' : 's'}: ${envKeys.slice(0, 20).join(', ')}${envKeys.length > 20 ? ', …' : ''}`);
  }
  lines.push('');
  lines.push('Provider dashboard state (live env values, quotas, webhooks, DNS) is not visible from repo evidence — verify in the dashboard or through provider MCP.');
  lines.push('');

  return lines.join('\n');
}
